<template>
  <modal class="modal" title="Reserve tickets" size="lg" @close="onClose">
    <template slot="body">
      <form class="modal__form" @submit.prevent>
        <base-form-input label="Email">
          <input v-model="email" type="email" placeholder="citizen@example.com" />
          <span slot="hint" v-if="emailHint">{{ emailHint }}</span>
        </base-form-input>
        <base-form-input label="Tickets">
          <select v-model="target">
            <option v-if="tiers.length > 0" :value="SHOW_TICKETS">Show tickets</option>
            <option v-if="meetAndGreet" :value="MEET_AND_GREET">{{ meetAndGreetLabel }}</option>
          </select>
        </base-form-input>
        <base-form-input v-if="target === SHOW_TICKETS" label="Tiers">
          <label class="modal__tier" v-for="tier in tiers" :key="tier.id">
            <input type="checkbox" :value="tier.id" v-model="selectedTierIds" />
            {{ tierLabel(tier) }}
          </label>
          <span slot="hint">Limit the reservation to the checked tiers — leave all unchecked to allow any tier</span>
        </base-form-input>
        <base-form-input v-if="target === MEET_AND_GREET" label="Quantity">
          <input v-model.number="quantity" type="number" min="1" />
          <span slot="hint" v-if="exceedsBy > 0" class="modal__warning"
            >This exceeds the remaining availability by {{ exceedsBy }} ticket{{ exceedsBy === 1 ? '' : 's' }}.
          </span>
        </base-form-input>
        <base-form-input label="Expires at (UTC)" :optional="true">
          <base-date-input v-model="expiresAt" type="datetime-local" :min="new Date()" />
          <span slot="hint">Leave empty to hold the tickets until the show</span>
        </base-form-input>
        <div class="modal__notify">
          <base-checkbox v-model="notify" :disabled="notifyRequired">Email the recipient</base-checkbox>
          <p class="modal__hint">{{ notifyHint }}</p>
        </div>
        <p class="modal__error" v-if="errorMessage">{{ errorMessage }}</p>
        <base-button
          class="modal__submit"
          :disabled="!canSubmit"
          :loading="loading"
          @click="onSubmit"
          v-html="notify ? 'Reserve and notify' : 'Reserve'"
        />
      </form>

      <div class="modal__table" v-if="reservations.length > 0">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Tickets</th>
              <th>Quantity</th>
              <th>Expires</th>
              <th>Notified</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="reservation in reservationsWithStatus" :key="reservation.id">
              <td>{{ reservation.email }}</td>
              <td>{{ reservationTicketsLabel(reservation) }}</td>
              <td>{{ reservation.quantity }}</td>
              <td>{{ reservation.expiresAt ? formatDate(reservation.expiresAt) : '–' }}</td>
              <td>{{ reservation.emailedAt ? formatDate(reservation.emailedAt) : '–' }}</td>
              <td>{{ reservation.status }}</td>
              <td>
                <base-button v-if="reservation.status === 'Pending'" @click="() => onCancel(reservation)">
                  Cancel
                </base-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <template slot="footer">
      <base-button class="modal__button" @click="onClose">Close</base-button>
    </template>
  </modal>
</template>

<script lang="ts">
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  Query,
  QueryUserArgs,
  QueryUserExistsArgs,
  Show,
  TicketReservation,
  TicketReservationType,
  TicketTier,
} from '../types/api';
import Modal from './Modal.vue';

dayjs.extend(utc);

const MEET_AND_GREET = 'meet-and-greet';
const SHOW_TICKETS = 'show-tickets';

const USER_EXISTS_QUERY = gql`
  query ReservationUserExists($input: UserExistsInput!) {
    userExists(input: $input)
  }
`;

const USER_SHOW_TICKETS_QUERY = gql`
  query ReservationUserShowTickets($email: String!) {
    user(email: $email) {
      id
      shows {
        show {
          id
        }
        tickets {
          id
          refundedAt
        }
      }
    }
  }
`;

@Component({
  name: 'ReserveTicketsModal',
  components: {
    Modal,
  },
  apollo: {
    show: {
      query: gql`
        query TicketReservationAvailability($showId: ID!) {
          show(id: $showId) {
            id
            ticketSales {
              tiers {
                id
                name
                price
                limit
                ticketsCount
                ticketsAvailableCount
              }
            }
            meetAndGreet {
              price
              purchaseQuota
              numberOfTicketsAvailableForPurchase
            }
          }
        }
      `,
      variables() {
        return { showId: this.showId };
      },
      fetchPolicy: 'network-only',
    },
    ticketReservations: {
      query: gql`
        query TicketReservations($showId: ID!) {
          ticketReservations(showId: $showId) {
            id
            email
            type
            quantity
            expiresAt
            emailedAt
            acceptedAt
            declinedAt
            isExpired
            isPending
            tiers {
              id
              name
            }
          }
        }
      `,
      variables() {
        return { showId: this.showId };
      },
      fetchPolicy: 'network-only',
    },
  },
})
export default class ReserveTicketsModal extends Vue {
  @Prop({ required: true }) showId!: string;

  public readonly MEET_AND_GREET = MEET_AND_GREET;
  public readonly SHOW_TICKETS = SHOW_TICKETS;

  show?: Show;
  ticketReservations?: TicketReservation[];

  email = '';
  userExists?: boolean;
  hasValidShowTicket = false;
  lookedUpEmail = '';
  emailLookupTimeout = 0;
  target: string = SHOW_TICKETS;
  selectedTierIds: string[] = [];
  quantity = 1;
  expiresAt: Date | null = null;
  notify = true;
  errorMessage: string | null = null;
  loading = false;

  get tiers() {
    return this.show?.ticketSales?.tiers || [];
  }

  get meetAndGreet() {
    return this.show?.meetAndGreet || null;
  }

  get selectedTiers(): TicketTier[] {
    if (this.selectedTierIds.length === 0) return this.tiers;
    return this.tiers.filter((tier) => this.selectedTierIds.includes(tier.id));
  }

  /** The recipient claims from whichever allowed tier has room, so the best case counts. */
  get available(): number {
    if (this.target === MEET_AND_GREET) return this.meetAndGreet?.numberOfTicketsAvailableForPurchase ?? 0;
    return Math.max(0, ...this.selectedTiers.map((tier) => tier.ticketsAvailableCount));
  }

  get exceedsBy(): number {
    return Math.max(0, this.quantity - this.available);
  }

  get isCompleteEmail() {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim());
  }

  get emailHint() {
    if (!this.isCompleteEmail || this.lookedUpEmail !== this.email.trim() || typeof this.userExists !== 'boolean') {
      return null;
    }
    if (this.hasValidShowTicket && this.target === SHOW_TICKETS) {
      return 'This user already has a citizen ticket for this show';
    }
    if (this.userExists) return 'This email is registered';
    if (this.target === MEET_AND_GREET) {
      return 'This email is not registered. They will receive a link to proceed with payment.';
    }
    return 'This email is not registered';
  }

  get notifyRequired() {
    return this.target === MEET_AND_GREET && this.lookedUpEmail === this.email.trim() && this.userExists === false;
  }

  get notifyHint() {
    if (this.notifyRequired) return 'Required — the payment link is only sent by email';
    return this.notify
      ? 'The recipient is emailed and prompted to register if they have no account'
      : 'Tickets are held without sending email';
  }

  get canSubmit(): boolean {
    if (this.loading || !this.email || this.quantity < 1 || !this.target) return false;
    return !(this.target === SHOW_TICKETS && this.hasValidShowTicket);
  }

  get reservations(): TicketReservation[] {
    return this.ticketReservations || [];
  }

  get reservationsWithStatus() {
    return this.reservations.map((reservation) => ({
      ...reservation,
      status: reservation.acceptedAt
        ? 'Accepted'
        : reservation.declinedAt
          ? 'Declined'
          : reservation.isExpired
            ? 'Expired'
            : 'Pending',
    }));
  }

  get meetAndGreetLabel() {
    if (!this.meetAndGreet) return '';
    return `Secret Gathering (${this.toUsd(this.meetAndGreet.price)}) – ${this.meetAndGreet.numberOfTicketsAvailableForPurchase} left`;
  }

  tierLabel(tier: TicketTier) {
    return `${tier.name} (${this.toUsd(tier.price)}) – ${tier.ticketsAvailableCount} left`;
  }

  reservationTicketsLabel(reservation: TicketReservation) {
    if (reservation.type === TicketReservationType.MeetAndGreet) return 'Secret Gathering';
    if (!reservation.tiers) return 'Any ticket';
    return reservation.tiers.map((tier) => tier.name).join(', ');
  }

  toUsd(value?: number) {
    return value != null ? `$${value / 1000}` : null;
  }

  formatDate(date: string | Date) {
    return `${dayjs.utc(date).format('YYYY-MM-DD HH:mm')} UTC`;
  }

  async onSubmit() {
    if (this.exceedsBy > 0) {
      const confirmed = await this.$modal.confirm({
        title: 'Exceeds quota',
        message: `This reservation exceeds the remaining availability by ${this.exceedsBy}. Reserve anyway?`,
      });
      if (!confirmed) return;
    }
    this.errorMessage = null;
    this.loading = true;
    try {
      await this.$apollo.mutate({
        mutation: gql`
          mutation CreateTicketReservation($input: CreateTicketReservationInput!) {
            createTicketReservation(input: $input) {
              ticketReservation {
                id
              }
            }
          }
        `,
        variables: {
          input: {
            email: this.email,
            showId: this.showId,
            type:
              this.target === MEET_AND_GREET ? TicketReservationType.MeetAndGreet : TicketReservationType.ShowTicket,
            tierIds: this.target === SHOW_TICKETS && this.selectedTierIds.length > 0 ? this.selectedTierIds : null,
            quantity: this.target === SHOW_TICKETS ? 1 : this.quantity,
            expiresAt: this.expiresAt || null,
            notify: this.notifyRequired || this.notify,
          },
        },
        refetchQueries: ['TicketReservations', 'TicketReservationAvailability', 'Tour'],
        awaitRefetchQueries: true,
      });
      this.email = '';
      this.quantity = 1;
      this.expiresAt = null;
      this.selectedTierIds = [];
      this.notify = true;
    } catch (e: any) {
      this.errorMessage = e?.graphQLErrors?.[0]?.message || 'Something went wrong, please try again';
    } finally {
      this.loading = false;
    }
  }

  async onCancel(reservation: TicketReservation) {
    const confirmed = await this.$modal.confirm({
      title: 'Cancel reservation?',
      message: `The tickets reserved for ${reservation.email} will be released. Continue?`,
    });
    if (!confirmed) return;
    await this.$apollo.mutate({
      mutation: gql`
        mutation CancelTicketReservation($input: CancelTicketReservationInput!) {
          cancelTicketReservation(input: $input) {
            ticketReservation {
              id
              declinedAt
            }
          }
        }
      `,
      variables: { input: { id: reservation.id } },
      refetchQueries: ['TicketReservations', 'TicketReservationAvailability', 'Tour'],
      awaitRefetchQueries: true,
    });
  }

  onClose() {
    this.$emit('close');
  }

  @Watch('show')
  onShowLoaded() {
    this.target = this.tiers.length > 0 ? SHOW_TICKETS : MEET_AND_GREET;
  }

  @Watch('target')
  onTargetChange() {
    if (this.target === SHOW_TICKETS) this.quantity = 1;
  }

  @Watch('notifyRequired')
  onNotifyRequired(required: boolean) {
    if (required) this.notify = true;
  }

  @Watch('email')
  onEmailChange() {
    this.lookedUpEmail = '';
    this.hasValidShowTicket = false;
    window.clearTimeout(this.emailLookupTimeout);
    const email = this.email.trim();
    if (!this.isCompleteEmail) return;
    this.emailLookupTimeout = window.setTimeout(() => {
      void this.lookupEmail(email);
    }, 400);
  }

  async lookupEmail(email: string) {
    const { data } = await this.$apollo.query<Pick<Query, 'userExists'>>({
      query: USER_EXISTS_QUERY,
      variables: { input: { email } } satisfies QueryUserExistsArgs,
      fetchPolicy: 'network-only',
    });
    if (this.email.trim() !== email) return;
    this.userExists = data.userExists;
    this.lookedUpEmail = email;
    this.hasValidShowTicket = data.userExists ? await this.lookupValidShowTicket(email) : false;
  }

  async lookupValidShowTicket(email: string) {
    const { data } = await this.$apollo.query<Pick<Query, 'user'>>({
      query: USER_SHOW_TICKETS_QUERY,
      variables: { email } satisfies QueryUserArgs,
      fetchPolicy: 'network-only',
    });
    if (this.email.trim() !== email) return this.hasValidShowTicket;
    return (data.user?.shows ?? []).some(
      (participation) =>
        participation.show.id === this.showId && participation.tickets.some((ticket) => !ticket.refundedAt)
    );
  }
}
</script>

<style lang="scss" scoped>
.modal {
  ::v-deep .modal__body {
    overflow-x: hidden;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
  }

  &__button {
    margin-left: 8px;
  }

  &__submit {
    align-self: flex-start;
    margin-top: 8px;
  }

  &__tier {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  &__warning {
    color: $color-theme-yellow;
  }

  &__error {
    color: $color-theme-red;
  }

  &__hint {
    font-size: 0.8em;
    color: $color-theme-red;
  }

  &__table {
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
  }

  table {
    width: max-content;
    min-width: 100%;
    border-collapse: collapse;

    th,
    td {
      border: 1px solid $color-theme-red;
      padding: 8px;
      white-space: nowrap;
    }

    th {
      background-color: #111;
    }

    tr:nth-child(even) {
      background-color: #111;
    }
  }
}
</style>
